import { Globe, Shield } from 'lucide-react';
import {
  loginWithGoogle,
  loginWithMicrosoft,
  loginWithYahoo,
} from '../firebase';
import { useState } from 'react';
import { AuthErrorModal } from './modals/AuthErrorModal';

export function LoginScreen() {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const baseUrl =
    import.meta.env.VITE_APP_BASE_URL || 'https://kowope.akandie.com';

  const handleLogin = async (providerLoginFunction: () => Promise<unknown>) => {
    setErrorMessage(null); // Clear previous errors
    try {
      await providerLoginFunction();
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err.code === 'auth/account-exists-with-different-credential') {
        setErrorMessage(
          'This email is already linked to an account with a different provider. Please try signing in with your original provider or link your accounts.'
        );
      } else {
        setErrorMessage(
          err.message || 'An unexpected error occurred during login.'
        );
      }
    }
  };

  const navigateToPrivacy = () => {
    window.history.pushState({}, '', '/privacy-policy');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 p-6 transition-colors duration-300 dark:bg-black">
      <div className="w-full max-w-md space-y-8 rounded-[40px] border border-zinc-100 bg-white p-10 text-center shadow-2xl shadow-zinc-200/50 transition-colors dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-black/20">
        <div className="space-y-4">
          <div className="mx-auto flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl">
            <img
              src="/images/logo-dark.webp"
              alt="Kówópé Logo"
              className="h-full w-full object-cover dark:hidden"
            />
            <img
              src="/images/logo-white.webp"
              alt="Kówópé Logo"
              className="hidden h-full w-full object-cover dark:block"
            />
          </div>
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
              Kówópé
            </h1>
            <p className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
              Take control of your finances with real-time tracking and smart
              insights.
            </p>
          </div>
        </div>
        <div className="space-y-3">
          <button
            onClick={() => handleLogin(loginWithGoogle)}
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-zinc-900 py-4 font-bold text-white shadow-lg transition-colors hover:bg-zinc-800 active:scale-95 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            <img
              src="/images/google-logo.webp"
              alt="Google"
              className="h-5 w-5"
            />
            Sign in with Google
          </button>
          <button
            onClick={() => handleLogin(loginWithMicrosoft)}
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#00a4ef] py-4 font-bold text-white shadow-lg transition-colors hover:bg-[#0078d4] active:scale-95"
          >
            <img
              src="/images/microsoft-logo-white.webp"
              alt="Microsoft"
              className="h-5 w-5"
            />
            Sign in with Microsoft
          </button>
          <button
            onClick={() => handleLogin(loginWithYahoo)}
            className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#6001d2] py-4 font-bold text-white shadow-lg transition-colors hover:bg-[#5000b0] active:scale-95"
          >
            <img
              src="/images/yahoo-logo-white.webp"
              alt="Yahoo"
              className="h-5 w-5"
            />
            Sign in with Yahoo
          </button>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-6">
        {baseUrl && (
          <a
            href={baseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs font-bold text-zinc-400 transition-colors hover:text-indigo-600"
          >
            <Globe className="h-3.5 w-3.5" />
            Visit our Website
          </a>
        )}

        <button
          onClick={navigateToPrivacy}
          className="flex items-center gap-2 text-xs font-bold text-zinc-400 transition-colors hover:text-indigo-600"
        >
          <Shield className="h-3.5 w-3.5" />
          Privacy Policy
        </button>
      </div>

      <AuthErrorModal
        isOpen={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage(null)}
      />
    </div>
  );
}
